import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import {
    AbstractControl,
    UntypedFormArray,
    UntypedFormBuilder,
    UntypedFormControl,
    UntypedFormGroup,
} from '@angular/forms';
import { USER_INPUT_DEBOUNCE_TIME } from '@modeso/aumico-lib-core-fe';
import {
    IEquityOptions,
    IFinancialLineItemOptions,
    IInputOptions,
    IMovementScheduleEditableColumnHeaders,
    ISuperTableOptions,
    SuperTableColumnType,
} from '@modeso/types__aumico-lib-notes-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { debounceTime } from 'rxjs/operators';
import { ITranslationsTableData } from '../../interfaces/translations-table-data.interface';

@UntilDestroy()
@Component({
    selector: 'aumico-notes-edit-notes-type-components-translations',
    templateUrl: './edit-notes-type-components-translations.component.html',
    styleUrls: ['./edit-notes-type-components-translations.component.scss'],
})
export class EditNotesTypeComponentsTranslationsComponent implements OnInit {
    public formGroup: UntypedFormGroup | undefined;
    public translationsFormArray: UntypedFormArray | undefined;
    public displayedColumns: string[] = ['name', 'currentLocale', 'selectedLocale'];

    @Input() tableData:
        | IEquityOptions
        | IMovementScheduleEditableColumnHeaders
        | IFinancialLineItemOptions
        | IInputOptions
        | ISuperTableOptions
        | undefined;
    @Input() currentLocale = 'de-ch';
    @Input() selectedLocale: string | undefined;

    @Output() translationValuesChanged: EventEmitter<ITranslationsTableData> =
        new EventEmitter<ITranslationsTableData>();

    constructor(private readonly formBuilder: UntypedFormBuilder) {}

    ngOnInit(): void {
        this.buildForm();
    }

    private buildForm(): void {
        this.formGroup = this.formBuilder.group({
            translationsFormArray: this.formBuilder.array([]),
        });
        this.translationsFormArray = this.getAllAsFormArray();

        this.monitorFormChanges();

        this.formGroup.setControl('translationsFormArray', this.translationsFormArray, { emitEvent: false });
        this.formGroup.updateValueAndValidity({ emitEvent: false });
    }

    private getAllAsFormArray(): UntypedFormArray {
        const formGroups: AbstractControl[] = this.getTableEntryFormGroup(this.tableData);
        return new UntypedFormArray(formGroups);
    }

    private getTableEntryFormGroup(
        data:
            | IEquityOptions
            | IMovementScheduleEditableColumnHeaders
            | IFinancialLineItemOptions
            | IInputOptions
            | ISuperTableOptions,
    ): AbstractControl[] {
        const dataSourceFormGroups: AbstractControl[] = [];

        Object.keys(data).forEach((key) => {
            if (
                key === 'totalRow' ||
                key === 'compareYears' ||
                key === 'dataType' ||
                key === 'numberOfDecimalPlaces' ||
                key === 'useAsParameter' ||
                key === 'parameterName'
            ) {
                return;
            }

            if (key === 'selectionOption') {
                data[key].forEach((value) => {
                    const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
                    dataSourceFormGroup['key'] = new UntypedFormControl(key);
                    dataSourceFormGroup['name'] = new UntypedFormControl(this.getTableEntryName(key));
                    dataSourceFormGroup['currentLocale'] = new UntypedFormControl(value[this.currentLocale]);
                    dataSourceFormGroup['selectedLocale'] = new UntypedFormControl(value[this.selectedLocale]);
                    dataSourceFormGroups.push(new UntypedFormGroup(dataSourceFormGroup));
                });
                return;
            }

            if (key === 'xAxisConfig') {
                let index = 1;
                data[key].forEach((value) => {
                    if (value.type === SuperTableColumnType.NUMBER || value.type === SuperTableColumnType.TEXT) {
                        const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
                        dataSourceFormGroup['key'] = new UntypedFormControl(this.getTableEntryDynamicName(key, index));
                        dataSourceFormGroup['name'] = new UntypedFormControl(this.getTableEntryDynamicName(key, index));
                        dataSourceFormGroup['currentLocale'] = new UntypedFormControl(value.title[this.currentLocale]);
                        dataSourceFormGroup['selectedLocale'] = new UntypedFormControl(
                            value.title[this.selectedLocale],
                        );
                        dataSourceFormGroups.push(new UntypedFormGroup(dataSourceFormGroup));
                        index = index + 1;
                    }
                });
                return;
            }

            if (key === 'segments') {
                let index = 1;
                data[key].forEach((value) => {
                    if (value.title != null) {
                        const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
                        dataSourceFormGroup['key'] = new UntypedFormControl(this.getTableEntryDynamicName(key, index));
                        dataSourceFormGroup['name'] = new UntypedFormControl(this.getTableEntryDynamicName(key, index));
                        dataSourceFormGroup['currentLocale'] = new UntypedFormControl(value.title[this.currentLocale]);
                        dataSourceFormGroup['selectedLocale'] = new UntypedFormControl(
                            value.title[this.selectedLocale],
                        );
                        dataSourceFormGroups.push(new UntypedFormGroup(dataSourceFormGroup));
                        index = index + 1;
                    }
                });
                return;
            }

            const dataSourceFormGroup: { [key: string]: AbstractControl } = {};
            dataSourceFormGroup['key'] = new UntypedFormControl(key);
            dataSourceFormGroup['name'] = new UntypedFormControl(this.getTableEntryName(key));
            dataSourceFormGroup['currentLocale'] = new UntypedFormControl(
                data[key] ? data[key][this.currentLocale] : data[key],
            );
            dataSourceFormGroup['selectedLocale'] = new UntypedFormControl(
                data[key] ? data[key][this.selectedLocale] : data[key],
            );
            dataSourceFormGroups.push(new UntypedFormGroup(dataSourceFormGroup));
        });
        return dataSourceFormGroups;
    }

    private getTableEntryName(key: string): string {
        switch (key) {
            case 'currentValueTitle1':
                return 'current value title 1';
            case 'currentValueTitle2':
                return 'current value title 2';
            case 'previousValueTitle1':
                return 'previous value title 1';
            case 'previousValueTitle2':
                return 'previous value title 2';
            case 'textTitle':
                return 'title';
            case 'column1':
                return 'column 1';
            case 'column2':
                return 'column 2';
            case 'column3':
                return 'column 3';
            case 'column4':
                return 'column 4';
            case 'text':
                return 'text';
            case 'selectionOption':
                return 'Selection option';
            case 'title':
                return 'title';
            default:
                return 'text';
        }
    }

    private getTableEntryDynamicName(key: string, index: number): string {
        switch (key) {
            case 'xAxisConfig':
                return 'freeValueTitle' + index;
            case 'segments':
                return 'segmentTitle' + index;
            default:
                return 'text' + index;
        }
    }

    private monitorFormChanges(): void {
        if (this.formGroup == null) {
            throw new Error('Form group is undefined.');
        }
        this.formGroup.valueChanges
            .pipe(debounceTime(USER_INPUT_DEBOUNCE_TIME), untilDestroyed(this))
            .subscribe((formValue) => {
                this.translationValuesChanged.emit(formValue.translationsFormArray);
            });
    }
}
